<script setup>
import { onMounted, onBeforeUnmount, ref } from 'vue';
import AiBotSettingsAPI from 'dashboard/api/aiBotSettings';
import TeamsAPI from 'dashboard/api/teams';
import InboxesAPI from 'dashboard/api/inboxes';

const loading = ref(false);
const saving = ref(false);
const success = ref('');
const error = ref('');
const teams = ref([]);
const inboxes = ref([]);
const usageLoading = ref(false);
const showUsageHistory = ref(false);

const evolutionLoading = ref(false);
const evolutionStatus = ref(null);
const evolutionQr = ref('');
const evolutionError = ref('');
const evolutionPhone = ref('');

const officialInboxes = ref([]);
const officialInboxesLoading = ref(false);
const selectedOfficialInboxId = ref(null);
const evolutionInstanceName = ref('');
const evolutionCreating = ref(false);

const evolutionProvision = ref({
  created: false,
  inboxFound: false,
  inboxId: null,
  inboxName: '',
  aiEnabled: false,
  teams: [],
});
let evolutionPollTimer = null;


const usage = ref({
  today: {
    cost_brl: 0,
    requests: 0,
  },
  month: {
    cost_brl: 0,
    requests: 0,
    input_tokens: 0,
    cached_input_tokens: 0,
    output_tokens: 0,
  },
  total: {
    cost_brl: 0,
    requests: 0,
  },
usd_brl_rate: 0,
current_month: '',
history: [],
});
const settings = ref({
  enabled: false,
  model: 'gpt-5.6-luna',
  assistant_name: 'Assistente Impulso',
  allowed_inbox_ids: [],
  whatsapp_provider: 'evolution',

  commercial_team_id: null,
  financial_team_id: null,
  support_team_id: null,
  cancellation_team_id: null,
  general_team_id: null,

  auto_transfer: true,
  add_waiting_label: true,
  stop_ai_after_transfer: true,

  system_prompt: '',
  transfer_message:
    'Vou encaminhar seu atendimento para o setor responsável. Aguarde um momento, por favor.',
});
const loadInboxes = async () => {
  try {
    const response = await InboxesAPI.get();

    inboxes.value =
      response.data?.payload ||
      response.data ||
      [];
  } catch (err) {
    console.error('Erro ao carregar caixas de entrada:', err);
  }
};

const loadTeams = async () => {
  try {
    const response = await TeamsAPI.get(true);
    teams.value = response.data || [];
  } catch (err) {
    console.error('Erro ao carregar times:', err);
  }
};
const loadSettings = async () => {
  loading.value = true;
  error.value = '';

  try {
    const response = await AiBotSettingsAPI.getSettings();

    settings.value = {
      ...settings.value,
      ...response.data,
    };
  } catch (err) {
    console.error('Erro ao carregar configurações da IA:', err);
    error.value = 'Não foi possível carregar as configurações da IA.';
  } finally {
    loading.value = false;
  }
};
const loadUsage = async () => {
  usageLoading.value = true;

  try {
    const response = await AiBotSettingsAPI.getUsageSummary();

    usage.value = {
      ...usage.value,
      ...response.data,
    };
  } catch (err) {
    console.error('Erro ao carregar consumo da IA:', err);
  } finally {
    usageLoading.value = false;
  }
};

const getEvolutionState = data => {
  return (
    data?.response?.instance?.state ||
    data?.response?.state ||
    data?.state ||
    ''
  );
};

const getEvolutionPhone = data => {
  const raw =
    data?.response?.instance?.ownerJid ||
    data?.response?.ownerJid ||
    data?.response?.instance?.number ||
    data?.response?.number ||
    '';

  return String(raw || '')
    .replace('@s.whatsapp.net', '')
    .replace(/\D/g, '');
};

const stopEvolutionPolling = () => {
  if (evolutionPollTimer) {
    clearInterval(evolutionPollTimer);
    evolutionPollTimer = null;
  }
};

const startEvolutionPolling = () => {
  stopEvolutionPolling();

  evolutionPollTimer = setInterval(async () => {
    await loadEvolutionStatus();

    if (getEvolutionState(evolutionStatus.value) === 'open') {
      evolutionQr.value = '';
      stopEvolutionPolling();
    }
  }, 5000);
};

const createEvolutionInstance = async () => {
  const instanceName = evolutionInstanceName.value.trim();

  if (!instanceName) {
    evolutionError.value = 'Informe o nome da instância.';
    return;
  }

  evolutionCreating.value = true;
  evolutionError.value = '';

  evolutionProvision.value = {
    created: false,
    inboxFound: false,
    inboxId: null,
    inboxName: '',
    aiEnabled: false,
    teams: [],
  };

  try {
    const response =
      await AiBotSettingsAPI.createEvolutionInstance(instanceName);

    const result = response.data || {};

    evolutionProvision.value = {
      created: result.created === true,
      inboxFound: !!result.inbox,
      inboxId: result.inbox?.id || null,
      inboxName: result.inbox?.name || '',
      aiEnabled: result.inbox?.ai_enabled === true,
      teams: Array.isArray(result.teams) ? result.teams : [],
    };

    evolutionInstanceName.value = '';

    await loadEvolutionStatus();
    await loadInboxes();

    if (result.inbox) {
      success.value =
        'Empresa provisionada com sucesso.';
    } else {
      success.value =
        'Evolution criada, mas a Inbox ainda não foi localizada.';
    }
  } catch (err) {
    console.error('Erro ao criar instância Evolution:', err);

    evolutionError.value =
      err.response?.data?.error ||
      err.response?.data?.message ||
      'Não foi possível criar a instância Evolution.';
  } finally {
    evolutionCreating.value = false;
  }
};

const loadOfficialWhatsappInboxes = async () => {
  officialInboxesLoading.value = true;

  try {
    const response =
      await AiBotSettingsAPI.getOfficialWhatsappInboxes();

    officialInboxes.value =
      response.data?.inboxes || [];

    const allowedIds =
      Array(settings.value.allowed_inbox_ids)
        .map(Number);

    const current =
      officialInboxes.value.find(inbox =>
        allowedIds.includes(Number(inbox.id))
      );

    if (current) {
      selectedOfficialInboxId.value = current.id;
    } else if (officialInboxes.value.length === 1) {
      selectedOfficialInboxId.value =
        officialInboxes.value[0].id;
    }
  } catch (err) {
    console.error(
      'Erro ao carregar Inboxes oficiais:',
      err
    );

    officialInboxes.value = [];
  } finally {
    officialInboxesLoading.value = false;
  }
};

const useOfficialInboxForAi = async () => {
  const inboxId =
    Number(selectedOfficialInboxId.value);

  if (!inboxId) {
    error.value =
      'Selecione uma Inbox oficial.';
    return;
  }

  error.value = '';
  success.value = '';

  try {
    const currentIds =
      Array(settings.value.allowed_inbox_ids)
        .map(Number)
        .filter(Boolean);

    // Ao escolher API Oficial, preservamos outras Inboxes
    // selecionadas, mas garantimos que a escolhida esteja ativa.
    const nextIds =
      [...new Set([...currentIds, inboxId])];

    settings.value.allowed_inbox_ids =
      nextIds;

    settings.value.whatsapp_provider =
      'official';

    await AiBotSettingsAPI.updateSettings(
      settings.value
    );

    success.value =
      'Inbox oficial vinculada à IA com sucesso.';

    await loadSettings();
    await loadInboxes();
  } catch (err) {
    console.error(
      'Erro ao vincular Inbox oficial:',
      err
    );

    error.value =
      err.response?.data?.error ||
      'Não foi possível vincular a Inbox oficial à IA.';
  }
};

const loadEvolutionStatus = async () => {
  evolutionLoading.value = true;
  evolutionError.value = '';

  try {
    const response = await AiBotSettingsAPI.getEvolutionStatus();
    evolutionStatus.value = response.data;
    evolutionPhone.value = getEvolutionPhone(response.data);

    if (getEvolutionState(response.data) === 'open') {
      evolutionQr.value = '';
      stopEvolutionPolling();
    }
  } catch (err) {
    console.error('Erro ao consultar Evolution:', err);
    evolutionError.value = 'Não foi possível consultar o status do WhatsApp.';
  } finally {
    evolutionLoading.value = false;
  }
};

const generateEvolutionQr = async () => {
  evolutionLoading.value = true;
  evolutionError.value = '';
  evolutionQr.value = '';

  try {
    const response = await AiBotSettingsAPI.getEvolutionQr();
    const data = response.data?.response || response.data || {};

    evolutionQr.value =
      data.base64 ||
      data.qrcode?.base64 ||
      data.qr ||
      data.code ||
      '';

    await loadEvolutionStatus();

    if (getEvolutionState(evolutionStatus.value) !== 'open') {
      startEvolutionPolling();
    }
  } catch (err) {
    console.error('Erro ao gerar QR Code:', err);
    evolutionError.value = 'Não foi possível gerar o QR Code.';
  } finally {
    evolutionLoading.value = false;
  }
};

const formatBRL = value => {
  return Number(value || 0).toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 2,
    maximumFractionDigits: 4,
  });
};

const formatNumber = value => {
  return Number(value || 0).toLocaleString('pt-BR');
};
const saveSettings = async () => {
  saving.value = true;
  success.value = '';
  error.value = '';

  try {
    const response = await AiBotSettingsAPI.updateSettings(
      settings.value
    );

    settings.value = {
      ...settings.value,
      ...response.data,
    };

    success.value = 'Configurações salvas com sucesso.';
  } catch (err) {
    console.error('Erro ao salvar configurações da IA:', err);
    error.value = 'Não foi possível salvar as configurações da IA.';
  } finally {
    saving.value = false;
  }
};
onMounted(async () => {
  await Promise.all([
    loadTeams(),
    loadInboxes(),
    loadSettings(),
    loadUsage(),
    loadEvolutionStatus(),
    loadOfficialWhatsappInboxes(),
  ]);
});

onBeforeUnmount(() => {
  stopEvolutionPolling();
});

</script>

<template>
  <div class="flex flex-col w-full h-full overflow-hidden bg-n-surface-1">
    <div class="px-8 py-6 border-b border-n-weak">
      <h1 class="text-2xl font-semibold text-n-slate-12">
        Configurações da IA
      </h1>

      <p class="mt-1 text-sm text-n-slate-11">
        Configure o atendimento automático da Impulso.
      </p>
    </div>

    <div class="flex-1 p-8 overflow-auto">
      <div
        v-if="loading"
        class="py-12 text-center text-n-slate-11"
      >
        Carregando configurações...
      </div>

      <div
        v-else
        class="max-w-4xl space-y-6"
      >
        <div
          v-if="success"
          class="p-4 rounded-lg bg-n-teal-3 text-n-teal-11"
        >
          {{ success }}
        </div>

        <div
          v-if="error"
          class="p-4 rounded-lg bg-n-ruby-3 text-n-ruby-11"
        >
          {{ error }}
        </div>
<div class="p-6 border rounded-xl border-n-weak bg-n-background">
  <div class="flex items-center justify-between mb-5">
    <div>
      <h2 class="text-lg font-semibold text-n-slate-12">
        Consumo da IA
      </h2>

      <p class="mt-1 text-sm text-n-slate-11">
        Valores estimados em Reais (R$).
      </p>
    </div>

<div class="flex items-center gap-2">
  <button
    type="button"
    class="px-3 py-2 text-sm border rounded-lg border-n-weak text-n-slate-12 hover:bg-n-alpha-2"
    @click="showUsageHistory = !showUsageHistory"
  >
    {{ showUsageHistory ? 'Ocultar histórico' : 'Ver histórico' }}
  </button>

  <button
    type="button"
    class="px-3 py-2 text-sm border rounded-lg border-n-weak text-n-slate-12 hover:bg-n-alpha-2"
    :disabled="usageLoading"
    @click="loadUsage"
  >
    {{ usageLoading ? 'Atualizando...' : 'Atualizar' }}
  </button>
</div>

  </div>

  <div class="grid grid-cols-1 gap-4 md:grid-cols-3">
    <div class="p-4 border rounded-lg border-n-weak">
      <div class="text-sm text-n-slate-11">
        Hoje
      </div>

      <div class="mt-1 text-2xl font-semibold text-n-slate-12">
        {{ formatBRL(usage.today.cost_brl) }}
      </div>

      <div class="mt-1 text-xs text-n-slate-10">
        {{ formatNumber(usage.today.requests) }} requisição(ões)
      </div>
    </div>

    <div class="p-4 border rounded-lg border-n-weak">
      <div class="text-sm text-n-slate-11">
        Este mês
      </div>

      <div class="mt-1 text-2xl font-semibold text-n-slate-12">
        {{ formatBRL(usage.month.cost_brl) }}
      </div>

      <div class="mt-1 text-xs text-n-slate-10">
        {{ formatNumber(usage.month.requests) }} requisição(ões)
      </div>
    </div>

    <div class="p-4 border rounded-lg border-n-weak">
      <div class="text-sm text-n-slate-11">
        Total acumulado
      </div>

      <div class="mt-1 text-2xl font-semibold text-n-slate-12">
        {{ formatBRL(usage.total.cost_brl) }}
      </div>

      <div class="mt-1 text-xs text-n-slate-10">
        {{ formatNumber(usage.total.requests) }} requisição(ões)
      </div>
    </div>
  </div>

  <div class="grid grid-cols-1 gap-4 mt-4 md:grid-cols-4">
    <div class="p-4 border rounded-lg border-n-weak">
      <div class="text-xs text-n-slate-11">
        Tokens de entrada
      </div>

      <div class="mt-1 font-semibold text-n-slate-12">
        {{ formatNumber(usage.month.input_tokens) }}
      </div>
    </div>

    <div class="p-4 border rounded-lg border-n-weak">
      <div class="text-xs text-n-slate-11">
        Tokens em cache
      </div>

      <div class="mt-1 font-semibold text-n-slate-12">
        {{ formatNumber(usage.month.cached_input_tokens) }}
      </div>
    </div>

    <div class="p-4 border rounded-lg border-n-weak">
      <div class="text-xs text-n-slate-11">
        Tokens de saída
      </div>

      <div class="mt-1 font-semibold text-n-slate-12">
        {{ formatNumber(usage.month.output_tokens) }}
      </div>
    </div>

    <div class="p-4 border rounded-lg border-n-weak">
      <div class="text-xs text-n-slate-11">
        Cotação USD/BRL
      </div>

      <div class="mt-1 font-semibold text-n-slate-12">
        R$ {{ Number(usage.usd_brl_rate || 0).toFixed(4) }}
      </div>

      <div class="mt-1 text-xs text-n-slate-10">
        {{ usage.current_month || '' }}
      </div>
    </div>
  </div>
<div
  v-if="showUsageHistory"
  class="mt-6 overflow-hidden border rounded-lg border-n-weak"
>
  <div class="px-4 py-3 border-b border-n-weak bg-n-alpha-1">
    <h3 class="font-medium text-n-slate-12">
      Histórico mensal
    </h3>
  </div>

  <div
    v-if="!usage.history || !usage.history.length"
    class="p-4 text-sm text-center text-n-slate-11"
  >
    Nenhum histórico disponível.
  </div>

  <div
    v-else
    class="overflow-x-auto"
  >
    <table class="w-full text-sm">
      <thead>
        <tr class="border-b border-n-weak text-n-slate-11">
          <th class="px-4 py-3 text-left">
            Mês
          </th>
          <th class="px-4 py-3 text-right">
            Requisições
          </th>
          <th class="px-4 py-3 text-right">
            Entrada
          </th>
          <th class="px-4 py-3 text-right">
            Cache
          </th>
          <th class="px-4 py-3 text-right">
            Saída
          </th>
          <th class="px-4 py-3 text-right">
            Custo
          </th>
        </tr>
      </thead>

      <tbody>
        <tr
          v-for="row in usage.history"
          :key="row.month"
          class="border-b border-n-weak last:border-b-0"
        >
          <td class="px-4 py-3 text-n-slate-12">
            {{ row.month }}
          </td>

          <td class="px-4 py-3 text-right text-n-slate-12">
            {{ formatNumber(row.requests) }}
          </td>

          <td class="px-4 py-3 text-right text-n-slate-12">
            {{ formatNumber(row.input_tokens) }}
          </td>

          <td class="px-4 py-3 text-right text-n-slate-12">
            {{ formatNumber(row.cached_input_tokens) }}
          </td>

          <td class="px-4 py-3 text-right text-n-slate-12">
            {{ formatNumber(row.output_tokens) }}
          </td>

          <td class="px-4 py-3 text-right font-medium text-n-slate-12">
            {{ formatBRL(row.cost_brl) }}
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</div>
</div>
        <div class="p-6 border rounded-xl border-n-weak bg-n-background">
          <h2 class="mb-5 text-lg font-semibold text-n-slate-12">
            Geral
          </h2>

          <div class="space-y-5">
            <label class="flex items-center justify-between gap-4">
              <div>
                <div class="font-medium text-n-slate-12">
                  IA habilitada
                </div>
                <div class="text-sm text-n-slate-11">
                  Permite que a assistente responda automaticamente.
                </div>
              </div>

              <input
                v-model="settings.enabled"
                type="checkbox"
                class="w-5 h-5"
              />
            </label>

            <div>
              <label class="block mb-1 text-sm font-medium text-n-slate-12">
                Nome da assistente
              </label>

              <input
                v-model="settings.assistant_name"
                type="text"
                class="w-full px-3 py-2 border rounded-lg border-n-weak bg-n-background text-n-slate-12"
              />
            </div>

            <div>
              <label class="block mb-1 text-sm font-medium text-n-slate-12">
                Modelo
              </label>

              <select
                v-model="settings.model"
                class="w-full px-3 py-2 border rounded-lg border-n-weak bg-n-background text-n-slate-12"
              >
                <option value="gpt-5.6-luna">
                  GPT-5.6 Luna
                </option>
                <option value="gpt-5.6">
                  GPT-5.6
                </option>
              </select>
            </div>
          </div>
        </div>

        <div class="p-6 border rounded-xl border-n-weak bg-n-background">
          <h2 class="text-lg font-semibold text-n-slate-12">
            Provedor do WhatsApp
          </h2>

          <p class="mt-1 mb-5 text-sm text-n-slate-11">
            Escolha como esta empresa irá conectar o WhatsApp ao Chatwoot.
          </p>

          <div class="grid grid-cols-1 gap-3 md:grid-cols-2">
            <label
              class="flex items-start gap-3 p-4 border rounded-lg cursor-pointer border-n-weak"
            >
              <input
                v-model="settings.whatsapp_provider"
                type="radio"
                value="evolution"
                class="mt-1"
              />

              <div>
                <div class="font-medium text-n-slate-12">
                  Evolution API
                </div>

                <div class="mt-1 text-sm text-n-slate-11">
                  Conexão via QR Code utilizando a Evolution.
                </div>
              </div>
            </label>

            <label
              class="flex items-start gap-3 p-4 border rounded-lg cursor-pointer border-n-weak"
            >
              <input
                v-model="settings.whatsapp_provider"
                type="radio"
                value="official"
                class="mt-1"
              />

              <div>
                <div class="font-medium text-n-slate-12">
                  API Oficial do WhatsApp
                </div>

                <div class="mt-1 text-sm text-n-slate-11">
                  Conexão oficial Meta através de uma Inbox do Chatwoot.
                </div>
              </div>
            </label>
          </div>

          <p class="mt-4 text-xs text-n-slate-10">
            Clique em Salvar configurações após alterar o provedor.
          </p>
        </div>

        <div
          v-if="settings.whatsapp_provider === 'evolution'"
          class="p-6 border rounded-xl border-n-weak bg-n-background"
        >
          <div class="flex items-start justify-between gap-4">
            <div>
              <h2 class="text-lg font-semibold text-n-slate-12">
                WhatsApp / Evolution
              </h2>

              <p class="mt-1 text-sm text-n-slate-11">
                Gerencie a conexão do WhatsApp desta empresa.
              </p>
            </div>

            <button
              type="button"
              class="px-3 py-2 text-sm border rounded-lg border-n-weak text-n-slate-12 hover:bg-n-alpha-2"
              :disabled="evolutionLoading"
              @click="loadEvolutionStatus"
            >
              {{ evolutionLoading ? 'Consultando...' : 'Atualizar status' }}
            </button>
          </div>

          <div class="mt-5">
            <div
              v-if="evolutionError"
              class="p-3 mb-4 text-sm rounded-lg bg-n-ruby-3 text-n-ruby-11"
            >
              {{ evolutionError }}
            </div>

            <div
              v-if="!evolutionStatus?.configured"
              class="p-4 mb-5 border rounded-lg border-n-weak bg-n-alpha-1"
            >
              <div class="mb-2 text-sm font-semibold text-n-slate-12">
                Provisionar empresa
              </div>

              <p class="mb-3 text-sm text-n-slate-11">
                Configure automaticamente o WhatsApp, a Inbox, a IA e os times desta empresa.
              </p>

              <div class="p-3 mb-4 text-sm border rounded-lg border-n-weak bg-n-alpha-1">
                <div class="mb-2 font-medium text-n-slate-12">
                  O provisionamento irá configurar:
                </div>

                <div class="space-y-1 text-n-slate-11">
                  <div>• Instância Evolution</div>
                  <div>• Integração com o Chatwoot</div>
                  <div>• Inbox da empresa</div>
                  <div>• IA ativada na Inbox</div>
                  <div>• Time comercial</div>
                  <div>• Time suporte</div>
                  <div>• Time financeiro</div>
                  <div>• Time cancelamento</div>
                  <div>• Time atendimento</div>
                </div>
              </div>

              <label class="block mb-1 text-sm font-medium text-n-slate-12">
                Nome da conexão WhatsApp
              </label>

              <div class="flex gap-2">
                <input
                  v-model="evolutionInstanceName"
                  type="text"
                  placeholder="Ex.: Empresa ABC - WhatsApp"
                  class="flex-1 px-3 py-2 border rounded-lg border-n-weak bg-n-background text-n-slate-12"
                  @keyup.enter="createEvolutionInstance"
                />

                <button
                  type="button"
                  class="px-4 py-2 text-sm font-medium text-white rounded-lg bg-n-brand disabled:opacity-50"
                  :disabled="evolutionCreating"
                  @click="createEvolutionInstance"
                >
                  {{
                    evolutionCreating
                      ? 'Provisionando...'
                      : 'Provisionar empresa'
                  }}
                </button>
              </div>
            </div>

            <div class="flex items-center gap-3">
              <span
                class="inline-block w-3 h-3 rounded-full"
                :class="
                  getEvolutionState(evolutionStatus) === 'open'
                    ? 'bg-n-teal-9'
                    : 'bg-n-ruby-9'
                "
              />

              <div>
                <div class="font-medium text-n-slate-12">
                  {{
                    getEvolutionState(evolutionStatus) === 'open'
                      ? 'Conectado'
                      : 'Desconectado'
                  }}
                </div>

                <div class="text-sm text-n-slate-11">
                  Instância:
                  {{
                    evolutionStatus?.instance ||
                    settings.evolution_instance_name ||
                    'Não configurada'
                  }}
                </div>

                <div
                  v-if="evolutionPhone"
                  class="text-sm text-n-slate-11"
                >
                  Número: {{ evolutionPhone }}
                </div>
              </div>
            </div>

            <div
              v-if="evolutionProvision.created"
              class="p-4 mt-5 border rounded-lg border-n-weak bg-n-alpha-1"
            >
              <div class="mb-3 text-sm font-semibold text-n-slate-12">
                Provisionamento
              </div>

              <div class="space-y-2 text-sm">
                <div class="flex items-center gap-2">
                  <span class="text-n-teal-10">●</span>
                  <span class="text-n-slate-12">
                    Instância Evolution criada
                  </span>
                </div>

                <div
                  v-if="evolutionProvision.inboxFound"
                  class="flex items-center gap-2"
                >
                  <span class="text-n-teal-10">●</span>
                  <span class="text-n-slate-12">
                    Inbox: {{ evolutionProvision.inboxName }}
                  </span>
                </div>

                <div
                  v-else
                  class="flex items-center gap-2"
                >
                  <span class="text-n-amber-10">●</span>
                  <span class="text-n-slate-12">
                    Inbox ainda não localizada
                  </span>
                </div>

                <div
                  v-if="evolutionProvision.aiEnabled"
                  class="flex items-center gap-2"
                >
                  <span class="text-n-teal-10">●</span>
                  <span class="text-n-slate-12">
                    IA ativada nesta Inbox
                  </span>
                </div>

                <div
                  v-if="evolutionProvision.teams.length"
                  class="pt-2 mt-2 border-t border-n-weak"
                >
                  <div class="mb-2 font-medium text-n-slate-12">
                    Times configurados
                  </div>

                  <div
                    v-for="team in evolutionProvision.teams"
                    :key="team.id"
                    class="flex items-center gap-2 mb-1"
                  >
                    <span class="text-n-teal-10">●</span>

                    <span class="text-n-slate-12">
                      {{ team.name }}
                    </span>

                    <span class="text-xs text-n-slate-10">
                      ID {{ team.id }}
                    </span>
                  </div>
                </div>

                <div
                  v-else-if="evolutionProvision.inboxFound"
                  class="flex items-center gap-2"
                >
                  <span class="text-n-amber-10">●</span>
                  <span class="text-n-slate-12">
                    Inbox localizada, mas vínculo com IA não confirmado
                  </span>
                </div>
              </div>
            </div>

            <div class="mt-5">
              <button
                v-if="
                  evolutionStatus?.configured &&
                  getEvolutionState(evolutionStatus) !== 'open'
                "
                type="button"
                class="px-4 py-2 text-sm font-medium text-white rounded-lg bg-n-brand disabled:opacity-50"
                :disabled="evolutionLoading"
                @click="generateEvolutionQr"
              >
                Gerar QR Code
              </button>

              <div
                v-if="getEvolutionState(evolutionStatus) === 'open'"
                class="text-sm font-medium text-n-teal-11"
              >
                WhatsApp conectado
              </div>
            </div>

            <div
              v-if="evolutionQr"
              class="mt-5"
            >
              <div class="mb-2 text-sm font-medium text-n-slate-12">
                Leia o QR Code com o WhatsApp
              </div>

              <img
                :src="
                  evolutionQr.startsWith('data:')
                    ? evolutionQr
                    : `data:image/png;base64,${evolutionQr}`
                "
                alt="QR Code WhatsApp"
                class="w-64 h-64 p-2 bg-white border rounded-lg border-n-weak"
              />
            </div>
          </div>
        </div>

        <div
          v-if="settings.whatsapp_provider === 'official'"
          class="p-6 border rounded-xl border-n-weak bg-n-background"
        >
          <div class="flex items-start justify-between gap-4">
            <div>
              <h2 class="text-lg font-semibold text-n-slate-12">
                WhatsApp Oficial
              </h2>

              <p class="mt-1 text-sm text-n-slate-11">
                Utilize uma Inbox WhatsApp Cloud API configurada no Chatwoot.
              </p>
            </div>

            <button
              type="button"
              class="px-3 py-2 text-sm border rounded-lg border-n-weak text-n-slate-12 hover:bg-n-alpha-2"
              :disabled="officialInboxesLoading"
              @click="loadOfficialWhatsappInboxes"
            >
              {{
                officialInboxesLoading
                  ? 'Consultando...'
                  : 'Atualizar'
              }}
            </button>
          </div>

          <div
            v-if="officialInboxesLoading"
            class="mt-5 text-sm text-n-slate-11"
          >
            Procurando Inboxes oficiais...
          </div>

          <div
            v-else-if="!officialInboxes.length"
            class="p-4 mt-5 border rounded-lg border-n-weak bg-n-alpha-1"
          >
            <div class="font-medium text-n-slate-12">
              Nenhuma Inbox oficial encontrada
            </div>

            <p class="mt-1 text-sm text-n-slate-11">
              Crie uma Inbox do tipo WhatsApp Cloud API no Chatwoot.
              Depois volte aqui e clique em Atualizar.
            </p>
          </div>

          <div
            v-else
            class="mt-5"
          >
            <label class="block mb-1 text-sm font-medium text-n-slate-12">
              Inbox oficial
            </label>

            <select
              v-model="selectedOfficialInboxId"
              class="w-full px-3 py-2 border rounded-lg border-n-weak bg-n-background text-n-slate-12"
            >
              <option
                :value="null"
                disabled
              >
                Selecione uma Inbox
              </option>

              <option
                v-for="inbox in officialInboxes"
                :key="inbox.id"
                :value="inbox.id"
              >
                {{
                  inbox.phone_number
                    ? `${inbox.name} - ${inbox.phone_number}`
                    : inbox.name
                }}
              </option>
            </select>

            <div
              v-if="selectedOfficialInboxId"
              class="p-4 mt-4 border rounded-lg border-n-weak bg-n-alpha-1"
            >
              <template
                v-for="inbox in officialInboxes"
                :key="`details-${inbox.id}`"
              >
                <div
                  v-if="
                    Number(inbox.id) ===
                    Number(selectedOfficialInboxId)
                  "
                >
                  <div class="text-sm text-n-slate-12">
                    <span class="font-medium">Inbox:</span>
                    {{ inbox.name }}
                  </div>

                  <div
                    v-if="inbox.phone_number"
                    class="mt-1 text-sm text-n-slate-12"
                  >
                    <span class="font-medium">Número:</span>
                    {{ inbox.phone_number }}
                  </div>

                  <div class="mt-1 text-sm text-n-teal-11">
                    WhatsApp Cloud API
                  </div>
                </div>
              </template>
            </div>

            <button
              type="button"
              class="px-4 py-2 mt-4 text-sm font-medium text-white rounded-lg bg-n-brand disabled:opacity-50"
              :disabled="!selectedOfficialInboxId"
              @click="useOfficialInboxForAi"
            >
              Usar esta Inbox na IA
            </button>
          </div>
        </div>

        <div class="p-6 border rounded-xl border-n-weak bg-n-background">
          <h2 class="mb-2 text-lg font-semibold text-n-slate-12">
            Caixas de entrada com IA
          </h2>

          <p class="mb-5 text-sm text-n-slate-11">
            Selecione em quais caixas de entrada a assistente pode responder automaticamente.
          </p>

          <div
            v-if="!inboxes.length"
            class="text-sm text-n-slate-11"
          >
            Nenhuma caixa de entrada disponível nesta empresa.
          </div>

          <div
            v-else
            class="space-y-3"
          >
            <label
              v-for="inbox in inboxes"
              :key="inbox.id"
              class="flex items-center justify-between gap-4 p-3 border rounded-lg border-n-weak"
            >
              <div>
                <div class="font-medium text-n-slate-12">
                  {{ inbox.name }}
                </div>

                <div class="text-xs text-n-slate-10">
                  ID {{ inbox.id }}
                </div>
              </div>

              <input
                v-model="settings.allowed_inbox_ids"
                type="checkbox"
                :value="inbox.id"
                class="w-5 h-5"
              />
            </label>
          </div>
        </div>

        <div class="p-6 border rounded-xl border-n-weak bg-n-background">
  <h2 class="mb-5 text-lg font-semibold text-n-slate-12">
    Times
  </h2>

  <p class="mb-5 text-sm text-n-slate-11">
    Selecione os times responsáveis por cada tipo de atendimento.
  </p>

  <div class="grid grid-cols-1 gap-4 md:grid-cols-2">
    <div>
      <label class="block mb-1 text-sm text-n-slate-12">
        Comercial
      </label>

      <select
        v-model.number="settings.commercial_team_id"
        class="w-full px-3 py-2 border rounded-lg border-n-weak bg-n-background text-n-slate-12"
      >
        <option :value="null">
          Selecione um time
        </option>

        <option
          v-for="team in teams"
          :key="team.id"
          :value="team.id"
        >
          {{ team.name }}
        </option>
      </select>
    </div>

    <div>
      <label class="block mb-1 text-sm text-n-slate-12">
        Financeiro
      </label>

      <select
        v-model.number="settings.financial_team_id"
        class="w-full px-3 py-2 border rounded-lg border-n-weak bg-n-background text-n-slate-12"
      >
        <option :value="null">
          Selecione um time
        </option>

        <option
          v-for="team in teams"
          :key="team.id"
          :value="team.id"
        >
          {{ team.name }}
        </option>
      </select>
    </div>

    <div>
      <label class="block mb-1 text-sm text-n-slate-12">
        Suporte Técnico
      </label>

      <select
        v-model.number="settings.support_team_id"
        class="w-full px-3 py-2 border rounded-lg border-n-weak bg-n-background text-n-slate-12"
      >
        <option :value="null">
          Selecione um time
        </option>

        <option
          v-for="team in teams"
          :key="team.id"
          :value="team.id"
        >
          {{ team.name }}
        </option>
      </select>
    </div>

    <div>
      <label class="block mb-1 text-sm text-n-slate-12">
        Cancelamento
      </label>

      <select
        v-model.number="settings.cancellation_team_id"
        class="w-full px-3 py-2 border rounded-lg border-n-weak bg-n-background text-n-slate-12"
      >
        <option :value="null">
          Selecione um time
        </option>

        <option
          v-for="team in teams"
          :key="team.id"
          :value="team.id"
        >
          {{ team.name }}
        </option>
      </select>
    </div>

    <div>
      <label class="block mb-1 text-sm text-n-slate-12">
        Atendimento Geral
      </label>

      <select
        v-model.number="settings.general_team_id"
        class="w-full px-3 py-2 border rounded-lg border-n-weak bg-n-background text-n-slate-12"
      >
        <option :value="null">
          Selecione um time
        </option>

        <option
          v-for="team in teams"
          :key="team.id"
          :value="team.id"
        >
          {{ team.name }}
        </option>
      </select>
    </div>
  </div>
</div>

        <div class="p-6 border rounded-xl border-n-weak bg-n-background">
          <h2 class="mb-5 text-lg font-semibold text-n-slate-12">
            Transferência para humano
          </h2>

          <div class="space-y-5">
            <label class="flex items-center justify-between gap-4">
              <span class="text-n-slate-12">
                Transferir automaticamente
              </span>

              <input
                v-model="settings.auto_transfer"
                type="checkbox"
                class="w-5 h-5"
              />
            </label>

            <label class="flex items-center justify-between gap-4">
              <span class="text-n-slate-12">
                Adicionar etiqueta aguardando-atendente
              </span>

              <input
                v-model="settings.add_waiting_label"
                type="checkbox"
                class="w-5 h-5"
              />
            </label>

            <label class="flex items-center justify-between gap-4">
              <span class="text-n-slate-12">
                Parar IA após transferir
              </span>

              <input
                v-model="settings.stop_ai_after_transfer"
                type="checkbox"
                class="w-5 h-5"
              />
            </label>

            <div>
              <label class="block mb-1 text-sm font-medium text-n-slate-12">
                Mensagem de transferência
              </label>

              <textarea
                v-model="settings.transfer_message"
                rows="4"
                class="w-full px-3 py-2 border rounded-lg border-n-weak bg-n-background text-n-slate-12"
              />
            </div>
          </div>
        </div>

        <div class="p-6 border rounded-xl border-n-weak bg-n-background">
          <h2 class="mb-5 text-lg font-semibold text-n-slate-12">
            Prompt da IA
          </h2>

          <textarea
            v-model="settings.system_prompt"
            rows="16"
            placeholder="Digite aqui as regras e instruções da assistente..."
            class="w-full min-h-[500px] px-3 py-3 font-mono text-sm leading-6 border rounded-lg resize-y border-n-weak bg-n-background text-n-slate-12"
          />
        </div>

        <div class="flex justify-end pb-10">
          <button
            type="button"
            :disabled="saving"
            class="px-6 py-3 font-medium text-white rounded-lg bg-n-brand disabled:opacity-50"
            @click="saveSettings"
          >
            {{ saving ? 'Salvando...' : 'Salvar configurações' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>
